import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { formatCurrency, formatDate, formatErr } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";
import { ShieldCheck, Building2, AlertCircle } from "lucide-react";

const estadoColors = {
  preparacion: "#9C8135", en_curso: "#2A5C43", pausada: "#64748B",
  finalizada: "#3B5270", recepcionada: "#B0652D",
};

export default function PortalPublico() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/public/portal/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(formatErr(e)))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--legatum-bg)] text-[var(--legatum-text-muted)]">
        Cargando portal…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--legatum-bg)] px-6">
        <div className="legatum-card p-10 max-w-md text-center" data-testid="portal-error">
          <AlertCircle className="h-10 w-10 mx-auto mb-4 text-red-400" />
          <h1 className="text-xl font-semibold">Enlace no válido</h1>
          <p className="mt-2 text-sm text-[var(--legatum-text-secondary)]">
            {error || "Este portal ya no está disponible. Solicita un nuevo enlace al gestor."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--legatum-bg)] text-[var(--legatum-text)] legatum-grain">
      <header className="border-b border-[var(--legatum-border)] bg-[var(--legatum-surface)]/60 backdrop-blur-sm relative z-10">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-md flex items-center justify-center"
                 style={{ background: "linear-gradient(135deg,var(--legatum-accent),var(--legatum-accent-2))" }}>
              <span className="font-bold tracking-wider" style={{ color: "var(--legatum-accent-fg)" }}>L</span>
            </div>
            <div className="leading-tight">
              <div className="text-sm font-bold tracking-[0.25em]">LEGATUM CAPITAL</div>
              <div className="text-[10px] text-[var(--legatum-text-muted)] tracking-[0.3em]">PORTAL DEL CLIENTE</div>
            </div>
          </div>
          <Badge className="uppercase text-[10px] tracking-widest" style={{ background: "rgba(255,255,255,0.06)", color: "var(--legatum-text)" }}>
            <ShieldCheck className="h-3 w-3 mr-1" /> Acceso autenticado
          </Badge>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10 relative z-10" data-testid="portal-content">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="text-[10px] uppercase tracking-[0.32em] text-[var(--legatum-text-muted)]">
            {data.cliente.tipo}
          </div>
          <h1 className="mt-2 text-4xl sm:text-5xl font-bold tracking-tight">
            Hola, {data.cliente.nombre}.
          </h1>
          <p className="mt-3 text-[var(--legatum-text-secondary)] max-w-2xl">
            Aquí tienes el estado actual de las obras compartidas contigo. Esta información se actualiza en tiempo real.
          </p>
        </motion.div>

        <div className="mt-10 space-y-4" data-testid="portal-obras">
          {data.obras.length === 0 && (
            <div className="legatum-card p-10 text-center text-[var(--legatum-text-muted)]">
              Aún no hay obras compartidas.
            </div>
          )}
          {data.obras.map((o, i) => {
            const pct = Math.max(0, Math.min(100, Number(o.porcentaje_ejecucion || 0)));
            const certPct = o.certificacion_prevista > 0
              ? Math.round((Number(o.certificacion_real || 0) / Number(o.certificacion_prevista)) * 100)
              : 0;
            const color = estadoColors[o.estado] || "#94A3B8";
            return (
              <motion.div
                key={o.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="legatum-card p-6"
                data-testid={`portal-obra-${o.id}`}
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex items-start gap-3">
                    <div className="h-11 w-11 rounded-lg bg-[var(--legatum-elevated)] flex items-center justify-center">
                      <Building2 className="h-5 w-5" style={{ color: "var(--legatum-accent)" }} />
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-[var(--legatum-text-muted)]">
                        {o.codigo}
                      </div>
                      <h2 className="text-xl font-bold mt-0.5">{o.nombre}</h2>
                      <div className="mt-1 text-sm text-[var(--legatum-text-secondary)]">
                        {o.director_obra ? `Dir. obra: ${o.director_obra}` : ""}
                      </div>
                    </div>
                  </div>
                  <Badge style={{ background: `${color}22`, color, border: `1px solid ${color}55` }} className="uppercase text-[10px] tracking-widest">
                    {String(o.estado || "—").replaceAll("_", " ")}
                  </Badge>
                </div>

                {/* progress bars */}
                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <div className="flex items-baseline justify-between mb-2">
                      <div className="text-[10px] uppercase tracking-widest text-[var(--legatum-text-muted)]">
                        Ejecución real
                      </div>
                      <div className="text-lg font-bold tabular-nums">{pct}%</div>
                    </div>
                    <div className="h-2 rounded-full bg-[var(--legatum-elevated)] overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${pct}%` }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        className="h-full"
                        style={{ background: "var(--legatum-accent)" }}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-baseline justify-between mb-2">
                      <div className="text-[10px] uppercase tracking-widest text-[var(--legatum-text-muted)]">
                        Certificación real / prevista
                      </div>
                      <div className="text-lg font-bold tabular-nums">{certPct}%</div>
                    </div>
                    <div className="text-xs text-[var(--legatum-text-secondary)] mb-2">
                      {formatCurrency(o.certificacion_real)} / {formatCurrency(o.certificacion_prevista)}
                    </div>
                    <div className="h-2 rounded-full bg-[var(--legatum-elevated)] overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${Math.min(certPct, 100)}%` }}
                        transition={{ duration: 0.8, delay: 0.35 }}
                        className="h-full"
                        style={{ background: color }}
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                  <Info label="Presupuesto" value={formatCurrency(o.presupuesto)} />
                  <Info label="Inicio" value={formatDate(o.fecha_inicio)} />
                  <Info label="Fin previsto" value={formatDate(o.fecha_fin_prevista)} />
                </div>
              </motion.div>
            );
          })}
        </div>

        <footer className="mt-12 pt-6 border-t border-[var(--legatum-border)] flex items-center justify-between text-xs text-[var(--legatum-text-muted)]">
          <div>© {new Date().getFullYear()} LEGATUM CAPITAL · Información confidencial</div>
          <div>Actualizado: {formatDate(data.generado)}</div>
        </footer>
      </main>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-[var(--legatum-text-muted)]">{label}</div>
      <div className="mt-1 font-semibold">{value}</div>
    </div>
  );
}
